import { fetchData, PaginationParameters, PaginationResponse } from "@request";
import { getQueryParams } from "@request/helpers";

export type AdminUser = {
  email: string;
  id: string;
  status: string;
  roles?: string[];
};

export type AdminUsersResponse = PaginationResponse<{
  users: AdminUser[];
}>;

export type AdminUsersRequest = PaginationParameters<{
  user_id?: string;
  email?: string;
  status?: string;
  role?: string;
}>;

export const getAdminUsersApi = async (data?: AdminUsersRequest) => {
  try {
    const queryParams = getQueryParams(data);
    const response = await fetchData<AdminUsersResponse>({
      url: `api/v1/admin/users?${queryParams}`,
    });

    return response;
  } catch (error) {
    console.error("getAdminUsersApi failed:", error);
    throw error;
  }
};

type UserInfo = {
  status: string;
  tg_group_id: number;
  role?: string;
  email?: string;
};

export type UserInfoBalance = {
  amount: number;
  id: string;
};

export type UserInfoEarnBalance = UserInfoBalance & {
  pay_in: number;
  pay_out: number;
};

export type TraderInfoResponse = {
  allow_farm_creation: boolean;
  allow_requisites_without_device: boolean;
  boosted: boolean;
  commission_config: CommissionConfig;
  ern_balance: UserInfoEarnBalance;
  frozen_balance: UserInfoBalance;
  save_balance: UserInfoBalance;
  trust_balance: UserInfoBalance;
  max_sum_order: number;
  min_sum_order: number;
  max_sum_pay_out_order: number;
  min_sum_pay_out_order: number;
  pay_in: boolean;
  pay_out: boolean;
  referrer_email: string | null;
  referrer_id: string | null;
  team_lead_commission_pay_in: number;
  // team_lead_commission_pay_out: number;

  // commission_card: number;
} & UserInfo;

export type AdminUserWallet = {
  address: string;
};

export type MerchantInfoResponse = {
  balance: UserInfoBalance;
  commission_config: CommissionConfig;
  commission_for_trader_pay_out: number | null;
  commission_pay_out: number;
  commission_withdrawal: number;
  max_sum_order: number;
  min_sum_order: number;
  max_sum_pay_out_order: number;
  min_sum_pay_out_order: number;
  pay_in: boolean;
  pay_out: boolean;
  referrer_email: string | null;
  referrer_id: string | null;
  team_lead_commission_pay_in: number | null;
  wallets: AdminUserWallet[];
} & UserInfo;

export type ReferralDetails = {
  earned_from_pay_in: number;
  earned_from_pay_out: number;
  linked_date: string;
  referral_email: string;
  referral_id: string;
  total_earned: number;
};

export type LeadUserInfo = {
  commission_withdrawal: number;
  referral_details: ReferralDetails[];
  // team_lead_commission_pay_in: number;
  // team_lead_commission_pay_out: number;
  total_pay_in_usdt: number;
  total_pay_out_usdt: number;
  total_usdt: number;
  wallets: AdminUserWallet[] | null;
} & UserInfo;

export type PlatformMerchantInfo = {
  commission_config: CommissionConfig;
  debt: number;
  id: string;
  merch_dept: number;
  merchant_email: string;
  our_ern_dept: number;
};

export type PlatformTransactionInfo = {
  amount: number;
  blockchain_url: string;
  created_at: string;
  created_at_unix: number;
  id: string;
  status: string;
  transaction_hash: string;
  wallet: string;
};

export type PlatformUserInfo = UserInfo & {
  frozen_balance: number;
  id: string;
  merchants: PlatformMerchantInfo[];
  orders_completed: number;
  orders_with_out_requisite_1000: number;
  orders_with_out_requisite_3000: number;
  orders_with_out_requisite_5000: number;
  orders_with_out_requisite_10000: number;
  requests_with_requisites: number;
  requests_without_requisites: number;
  send_orders_1000: number;
  send_orders_3000: number;
  send_orders_5000: number;
  send_orders_10000: number;
  token_for_callback: string;
  total_requests: number;
  trust_balance: number;
  transactions?: PlatformTransactionInfo[];
};

export type UserInfoResponse =
  | ({ kind: AdminUserTypes.Trader } & TraderInfoResponse)
  | ({ kind: AdminUserTypes.Merchant } & MerchantInfoResponse)
  | ({ kind: AdminUserTypes.Lead } & LeadUserInfo)
  | ({ kind: AdminUserTypes.Platform } & PlatformUserInfo)
  | null;

export enum AdminUserTypes {
  Trader = "trader",
  Merchant = "merchant",
  Lead = "lead",
  Platform = "platform",
}

export const getAdminUserInfoApi = async (userId: string) => {
  try {
    const response = await fetchData<UserInfoResponse>({
      url: `api/v1/admin/users/info/${userId}`,
    });

    switch (response?.role) {
      case "trader":
        response.kind = AdminUserTypes.Trader;
        break;
      case "merchant":
        response.kind = AdminUserTypes.Merchant;
        break;
      case "trader_teamlead":
      case "merchant_agent":
        response.kind = AdminUserTypes.Lead;
        break;
      case "platform":
        response.kind = AdminUserTypes.Platform;
        break;
      // retur response;
      default:
        break;
    }

    return response;
  } catch (error) {
    console.error("getAdminUsersApi failed:", error);
    throw error;
  }
};

export type CommissionConfig = {
  commission_sbp: CommissionField[];
  commission_card: CommissionField[];
  commission_account_number: CommissionField[];
  commission_ibt: CommissionField[];
};

export type CommissionField = {
  min_amount: number;
  max_amount: number;
  rate: number;
};

export type BaseUserInfoUpdateRequest = {
  status?: string;
  roles?: string[];
  referrer_email: string | null | undefined;
};

export type TraderUserInfoUpdateRequest = BaseUserInfoUpdateRequest & {
  kind: AdminUserTypes.Trader;
  allow_farm_creation: boolean;
  allow_requisites_without_device: boolean;
  boosted: boolean;
  commission_config: CommissionConfig;
  max_sum_order: number;
  min_sum_order: number;
  max_sum_pay_out_order: number;
  min_sum_pay_out_order: number;
  pay_in: boolean;
  pay_out: boolean;
  team_lead_commission_pay_in: number | undefined;
};

export type MerchantUserInfoUpdateRequest = BaseUserInfoUpdateRequest & {
  kind: AdminUserTypes.Merchant;
  commission_for_trader_pay_out?: number;
  commission_withdrawal?: number;
  commission_config: CommissionConfig;
  commission_pay_out: number;
  max_sum_order: number;
  min_sum_order: number;
  max_sum_pay_out_order: number;
  min_sum_pay_out_order: number;
  pay_in: boolean;
  pay_out: boolean;
  team_lead_commission_pay_in: number | undefined;
};

export type LeadUserInfoUpdateRequest = BaseUserInfoUpdateRequest & {
  kind: AdminUserTypes.Lead;
  commission_withdrawal?: number;
};

export type UserInfoUpdateRequest =
  | TraderUserInfoUpdateRequest
  | MerchantUserInfoUpdateRequest
  | LeadUserInfoUpdateRequest;

export const updateUserInfoApi = async (
  userId: string,
  data: UserInfoUpdateRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `api/v1/admin/users/info/${userId}`,
      method: "PUT",
      data,
    });
    return response;
  } catch (error) {
    console.error("getAdminUsersApi failed:", error);
    throw error;
  }
};

export type UserChangeBalanceRequest = {
  add_balance: number;
  balance_type: string;
  comment: string;
  current_balance: number;
  final_balance: number;
  type?: string;
  user: string;
};

export const changeUserBalanceApi = async (
  balanceId: string,
  data: UserChangeBalanceRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `api/v1/admin/change-balance/${balanceId}`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("changeUserBalanceApi failed:", error);
    throw error;
  }
};

export type ChangePasswordRequest = {
  new_password: string;
  user_id: string;
};

export const changeUserPasswordApi = async (data: ChangePasswordRequest) => {
  try {
    const response = await fetchData<string>({
      url: `api/v1/admin/users/change-password`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("changeUserPasswordApi failed:", error);
    throw error;
  }
};

export type resetUserTOTPRequest = {
  user_id: string;
};

export const resetUserTOTPApi = async (data: resetUserTOTPRequest) => {
  try {
    const response = await fetchData<string>({
      url: `api/v1/admin/users/reset-totp`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("resetUserTOTPApi failed:", error);
    throw error;
  }
};

export type AdminUserRole = {
  id: string;
  name: string;
};
export type AdminUserRolesResponse = AdminUserRole[];

export const getAdminUserRolesApi = async () => {
  try {
    const response = await fetchData<AdminUserRolesResponse>({
      url: `api/v1/admin/users/roles`,
    });

    return response;
  } catch (error) {
    console.error("getAdminUserRolesApi failed:", error);
    throw error;
  }
};

export type AddMerchantWalletsRequest = {
  addresses: string[];
  deleted_addresses: string[];
  user_id: string;
};

export const addMerchantWalletsApi = async (
  data: AddMerchantWalletsRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `api/v1/admin/white-wallets`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("addMerchantWalletsApi failed:", error);
    throw error;
  }
};

export type ActivateUserRequest = {
  // role_id: string;
  user_id: string;
};

export const activateUserApi = async (data: ActivateUserRequest) => {
  try {
    const response = await fetchData<string>({
      url: `api/v1/admin/users/activate`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("activateUserApi failed:", error);
    throw error;
  }
};

export type UpdateUserNotificationSettingsRequest = {
  telegram_chat_id: string;
};
export const updateUserNotificationSettingsApi = async (
  userId: string,
  data: UpdateUserNotificationSettingsRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `api/v1/users/${userId}/notifications/settings`,
      method: "PUT",
      data,
    });
    return response;
  } catch (error) {
    console.error("UpdateUserNotificationSettingsApi failed:", error);
    throw error;
  }
};
export type SetUserRoleRequest = {
  role: string;
};
export const setUserRoleApi = async (
  userId: string,
  data: SetUserRoleRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `/api/v1/admin/users/set_role/${userId}`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("UpdateUserNotificationSettingsApi failed:", error);
    throw error;
  }
};

export const getApiKeyApi = async (userId: string) => {
  try {
    const response = await fetchData<string>({
      url: `/api/v1/admin/api-key/${userId}`,
    });
    return response;
  } catch (error) {
    console.error("getApiKey failed:", error);
    throw error;
  }
};

export type PlatformTransactionsResponse = PaginationResponse<{
  transactions: PlatformTransactionInfo[];
}>;

export const getPlatformTransactionsApi = async (userId: string) => {
  try {
    const response = await fetchData<PlatformTransactionsResponse>({
      url: `/api/v1/admin/platforms/${userId}/transactions`,
    });
    return response;
  } catch (error) {
    console.error("getPlatformTransactionsApi failed:", error);
    throw error;
  }
};

export type AddMerchantToPlatformRequest = {
  commission_config: CommissionConfig;
  merchant_id: string;
};

export const addMerchantToPlatformApi = async (
  userId: string,
  data: AddMerchantToPlatformRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `/api/v1/admin/platforms/${userId}/merchants`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("addMerchantToPlatformApi failed:", error);
    throw error;
  }
};

export type EditMerchantToPlatformRequest = {
  merchants: AddMerchantToPlatformRequest[];
};

export const editMerchantToPlatformApi = async (
  userId: string,
  data: EditMerchantToPlatformRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `/api/v1/admin/platforms/${userId}/merchants`,
      method: "PUT",
      data,
    });
    return response;
  } catch (error) {
    console.error("addMerchantToPlatformApi failed:", error);
    throw error;
  }
};

export type AddTransactionToPlatformRequest = {
  amount: number;
  transaction_hash: string;
  wallet: string;
};

export const addTransactionToPlatformApi = async (
  userId: string,
  data: AddTransactionToPlatformRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `/api/v1/admin/platforms/${userId}/transactions`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("addTransactionToPlatformApi failed:", error);
    throw error;
  }
};

export type SetPlatformTeamleadComissionRequest = {
  referrer_email: string;
  team_lead_commission_pay_in: number;
};

export const setPlatformTeamleadComissionApi = async (
  userId: string,
  data: SetPlatformTeamleadComissionRequest
) => {
  try {
    const response = await fetchData<string>({
      url: `/api/v1/admin/platforms/${userId}/referral`,
      method: "POST",
      data,
    });
    return response;
  } catch (error) {
    console.error("setPlatformTeamleadComissionApi failed:", error);
    throw error;
  }
};
